import {
  Search,
  Lock,
  Bot,
  User,
  Wrench,
  Phone,
  Mail,
  ChevronLeft,
  LayoutDashboard,
  MessagesSquare,
  Users,
  Ticket,
  BarChart3,
  BookOpen,
  Workflow,
  Tag,
  FileText,
  Send,
  Sparkles,
  ArrowUpRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * CSS-rendered product mockups for the marketing landing page.
 *
 * These mirror the REAL customer app (frontend/src/routes/*): a Tailwind
 * list-table + detail-page review console — NOT a live multi-pane chat
 * inbox. Conversations are read-only transcripts carrying AI metadata and
 * knowledge citations; Leads + Tickets are the contact context; analytics
 * live on Dashboard + Insights. Built entirely from divs + lucide icons +
 * brand tokens (blue #2D71F3, navy #0B1832, gold, green) — no image assets,
 * no runnable-app dependency. All decorative (aria-hidden), never live.
 */

const APP_URL = 'aichat.pressone.africa'

/* ── Shared chrome + primitives ─────────────────────────────────────────── */

/** Browser-style window frame with a thin brand gradient edge + mac dots. */
export function BrowserFrame({
  children,
  path = '',
  className,
}: {
  children: React.ReactNode
  path?: string
  className?: string
}) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative overflow-hidden rounded-2xl bg-surface shadow-3',
        className,
      )}
    >
      <div
        className="h-1 w-full"
        style={{
          background:
            'linear-gradient(90deg, #2D71F3 0%, #3F70EB 35%, #1BDE6C 70%, #FFC20D 100%)',
        }}
      />
      <div className="flex items-center gap-3 border-b border-border bg-surface-2/70 px-4 py-2.5">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-brand-red/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-brand-gold/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-brand-green/80" />
        </div>
        <div className="flex flex-1 justify-center">
          <div className="inline-flex max-w-[80%] items-center gap-1.5 truncate rounded-md border border-border bg-surface px-3 py-1 text-[11px] text-muted">
            <Lock className="h-3 w-3" />
            {APP_URL}
            {path}
          </div>
        </div>
        <div className="w-9" />
      </div>
      {children}
    </div>
  )
}

function Avatar({ name, tone, className }: { name: string; tone: string; className?: string }) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white',
        tone,
        className,
      )}
    >
      {initials}
    </span>
  )
}

const CHANNEL_TONE: Record<string, string> = {
  WhatsApp: 'bg-brand-green-soft text-brand-green',
  Instagram: 'bg-brand-blue-soft text-brand-blue',
  Web: 'bg-brand-navy-soft text-brand-navy',
  SMS: 'bg-brand-gold-soft text-brand-gold',
}

function ChannelChip({ channel }: { channel: keyof typeof CHANNEL_TONE | string }) {
  return (
    <span
      className={cn(
        'inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
        CHANNEL_TONE[channel] ?? 'bg-surface-2 text-muted',
      )}
    >
      {channel}
    </span>
  )
}

const STATUS_TONE: Record<string, string> = {
  active: 'bg-brand-green-soft text-brand-green',
  handoff: 'bg-brand-gold-soft text-brand-gold',
  ended: 'bg-surface-2 text-muted',
  new: 'bg-brand-blue-soft text-brand-blue',
}

function StatusPill({ status }: { status: keyof typeof STATUS_TONE | string }) {
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-[9px] font-semibold capitalize',
        STATUS_TONE[status] ?? 'bg-surface-2 text-muted',
      )}
    >
      {status}
    </span>
  )
}

/* ── App shell + dashboard (hero centerpiece) ───────────────────────────── */

const NAV_GROUPS: { heading: string; items: { icon: typeof MessagesSquare; label: string }[] }[] = [
  {
    heading: 'Workspace',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard' },
      { icon: MessagesSquare, label: 'Conversations' },
      { icon: Users, label: 'Leads' },
      { icon: Ticket, label: 'Tickets' },
      { icon: BarChart3, label: 'Insights' },
    ],
  },
  {
    heading: 'Configure',
    items: [
      { icon: BookOpen, label: 'Knowledge' },
      { icon: Workflow, label: 'Chat Flows' },
      { icon: Tag, label: 'Intents' },
      { icon: Wrench, label: 'Tools' },
      { icon: FileText, label: 'Forms' },
    ],
  },
]

const STAT_CARDS = [
  { label: 'Conversations', value: '1,284', hint: 'All time', icon: MessagesSquare, tone: 'text-brand-navy' },
  { label: 'Leads', value: '312', hint: 'Captured leads', icon: Users, tone: 'text-brand-green' },
  { label: 'Messages', value: '4,920', hint: 'Last 30 days', icon: Send, tone: 'text-brand-blue' },
  { label: 'Knowledge items', value: '86', hint: 'In knowledge base', icon: BookOpen, tone: 'text-brand-gold' },
]

const RECENT = [
  { name: 'Paul Goodman', channel: 'WhatsApp', snippet: 'I made an enquiry last week abo…', status: 'active', tone: 'bg-brand-blue' },
  { name: 'Musa Tebomo', channel: 'Instagram', snippet: 'Are you open on the 5th?', status: 'handoff', tone: 'bg-brand-green' },
  { name: 'Maduka Ngozi', channel: 'WhatsApp', snippet: 'Deliver to Lekki on Saturday?', status: 'ended', tone: 'bg-brand-gold' },
]

/** Full app shell with the real left-nav + the dashboard overview screen. */
export function DashboardMock({ className }: { className?: string }) {
  return (
    <BrowserFrame className={className}>
      <div className="grid h-[clamp(380px,48vw,560px)] grid-cols-[3rem_1fr] bg-bg/40 sm:grid-cols-[13.5rem_1fr]">
        {/* Sidebar */}
        <aside className="flex min-h-0 flex-col border-r border-border bg-surface">
          <div className="flex items-center gap-2 px-4 py-3.5">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-brand-navy text-white">
              <Bot className="h-4 w-4" />
            </span>
            <span className="hidden text-[13px] font-semibold text-text-strong sm:inline">
              PressOne <span className="text-brand-blue">AI</span>
            </span>
          </div>
          <nav className="min-h-0 flex-1 space-y-4 overflow-hidden px-2.5 py-1">
            {NAV_GROUPS.map((g) => (
              <div key={g.heading}>
                <div className="hidden px-2 pb-1.5 text-[9px] uppercase tracking-[0.16em] text-text-soft sm:block">
                  {g.heading}
                </div>
                <div className="space-y-0.5">
                  {g.items.map((it) => {
                    const active = it.label === 'Dashboard'
                    return (
                      <div
                        key={it.label}
                        className={cn(
                          'relative flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[12px]',
                          active
                            ? 'bg-brand-blue-soft font-semibold text-brand-blue'
                            : 'text-muted',
                        )}
                      >
                        {active && (
                          <span className="absolute -left-2.5 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-brand-blue" />
                        )}
                        <it.icon className="h-4 w-4 shrink-0" />
                        <span className="hidden truncate sm:inline">{it.label}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>
          <div className="hidden border-t border-border p-3 sm:block">
            <div className="rounded-lg bg-brand-blue-soft/60 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wide text-brand-blue/80">
                Available credits
              </div>
              <div className="mt-0.5 flex items-center justify-between">
                <span className="text-[15px] font-semibold text-text-strong">8,420</span>
                <span className="rounded bg-brand-blue px-2 py-0.5 text-[10px] font-semibold text-white">
                  Top up
                </span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center justify-between border-b border-border bg-surface px-4 py-2.5">
            <div className="leading-tight">
              <div className="text-[9px] uppercase tracking-wide text-text-soft">Organization</div>
              <div className="text-[12px] font-semibold text-text-strong">Acme Stores</div>
            </div>
            <div className="flex items-center gap-2">
              <Avatar name="Bisi A" tone="bg-brand-navy" className="h-7 w-7" />
              <span className="hidden text-[12px] font-medium text-text sm:inline">Bisi A.</span>
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-hidden p-4">
            {/* Hero strip */}
            <div className="bg-brand-mesh rounded-xl px-4 py-3.5 text-white">
              <span className="inline-flex rounded-full bg-white/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
                Workspace overview
              </span>
              <div className="mt-1.5 text-[13px] font-semibold leading-snug">
                Welcome back. Your trained digital employee is on duty.
              </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
              {STAT_CARDS.map((s) => (
                <div key={s.label} className="rounded-xl border border-border bg-surface p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] uppercase tracking-wide text-text-soft">
                      {s.label}
                    </span>
                    <s.icon className={cn('h-3.5 w-3.5', s.tone)} />
                  </div>
                  <div className="mt-1 text-lg font-semibold text-text-strong">{s.value}</div>
                  <div className="text-[9px] text-text-soft">{s.hint}</div>
                </div>
              ))}
            </div>

            {/* Recent conversations */}
            <div className="rounded-xl border border-border bg-surface">
              <div className="flex items-center justify-between border-b border-border px-3 py-2">
                <span className="text-[11px] font-semibold text-text-strong">
                  Recent conversations
                </span>
                <span className="text-[10px] font-medium text-brand-blue">See all</span>
              </div>
              <div className="divide-y divide-border/70">
                {RECENT.map((r) => (
                  <div key={r.name} className="flex items-center gap-2.5 px-3 py-2">
                    <Avatar name={r.name} tone={r.tone} className="h-7 w-7" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-[11px] font-semibold text-text-strong">
                          {r.name}
                        </span>
                        <ChannelChip channel={r.channel} />
                      </div>
                      <div className="truncate text-[10px] text-muted">{r.snippet}</div>
                    </div>
                    <StatusPill status={r.status} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

/* ── Conversation transcript (read-only, with AI metadata + citations) ──── */

/** Read-only conversation detail: incoming/outgoing bubbles, AI metadata,
 *  knowledge citations — mirrors conversation-detail.tsx. No composer. */
export function ConversationMock({ className }: { className?: string }) {
  return (
    <BrowserFrame className={className} path="/conversations/cv_a91b">
      <div className="bg-bg/30">
        {/* Detail header */}
        <div className="border-b border-border bg-surface px-4 py-3">
          <div className="inline-flex items-center gap-1 text-[10px] text-muted">
            <ChevronLeft className="h-3 w-3" />
            Conversations
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-mono text-[13px] font-semibold text-text-strong">cv_a91b…</span>
            <ChannelChip channel="WhatsApp" />
            <StatusPill status="active" />
          </div>
        </div>

        <div className="space-y-3 px-4 py-4">
          {/* incoming */}
          <div className="flex items-start gap-2">
            <Avatar name="Paul Goodman" tone="bg-surface-2 !text-text-soft" className="h-6 w-6" />
            <div className="max-w-[78%]">
              <div className="rounded-2xl rounded-tl-sm border border-border bg-surface px-3 py-2 text-[11px] leading-relaxed text-text-strong">
                I made an enquiry last week but haven't received any confirmation yet.
              </div>
              <div className="mt-1 text-[9px] text-text-soft">09:42</div>
            </div>
          </div>

          {/* outgoing AI */}
          <div className="flex items-start justify-end gap-2">
            <div className="max-w-[78%]">
              <div className="rounded-2xl rounded-tr-sm bg-brand-navy px-3 py-2 text-[11px] leading-relaxed text-white">
                Your order #4821 shipped this morning and is out for delivery today. You'll get a
                confirmation SMS on arrival.
              </div>
              <div className="mt-1 flex items-center justify-end gap-2 text-[9px] text-text-soft">
                <span className="inline-flex items-center gap-0.5 rounded bg-brand-blue-soft px-1 py-0.5 font-semibold text-brand-blue">
                  <Sparkles className="h-2.5 w-2.5" />
                  AI
                </span>
                <span>128 tok</span>
                <span>640ms</span>
                <span>09:42</span>
              </div>
              {/* citations */}
              <div className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[9px] font-medium text-muted">
                <BookOpen className="h-3 w-3 text-brand-blue" />
                2 sources
                <span className="text-text-soft">· Shipping policy · Order #4821</span>
              </div>
            </div>
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue-soft text-brand-blue">
              <Bot className="h-3.5 w-3.5" />
            </span>
          </div>

          {/* incoming */}
          <div className="flex items-start gap-2">
            <Avatar name="Paul Goodman" tone="bg-surface-2 !text-text-soft" className="h-6 w-6" />
            <div className="max-w-[78%]">
              <div className="rounded-2xl rounded-tl-sm border border-border bg-surface px-3 py-2 text-[11px] leading-relaxed text-text-strong">
                Oh perfect, thank you so much!
              </div>
              <div className="mt-1 text-[9px] text-text-soft">09:43</div>
            </div>
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

/* ── Conversations list table ───────────────────────────────────────────── */

const ROWS = [
  { id: 'cv_a91b', channel: 'WhatsApp', name: 'Paul Goodman', phone: '+234 819…', msgs: 6, status: 'active' },
  { id: 'cv_7d4e', channel: 'Instagram', name: 'Musa Tebomo', phone: '+234 803…', msgs: 4, status: 'handoff' },
  { id: 'cv_2f10', channel: 'WhatsApp', name: 'Maduka Ngozi', phone: '+234 701…', msgs: 9, status: 'ended' },
  { id: 'cv_b8c3', channel: 'Web', name: 'Kal Krypton', phone: '—', msgs: 3, status: 'ended' },
  { id: 'cv_55a2', channel: 'SMS', name: 'Jane Hecht', phone: '+234 815…', msgs: 5, status: 'active' },
]

/** Conversations review console: filter bar + status/channel table. */
export function ConversationsTableMock({ className }: { className?: string }) {
  return (
    <BrowserFrame className={className} path="/conversations">
      <div className="bg-bg/30 p-3.5">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface p-2.5">
          <div className="inline-flex flex-1 items-center gap-1.5 rounded-md bg-surface-2 px-2 py-1.5 text-[10px] text-muted">
            <Search className="h-3 w-3" />
            Search ID or name…
          </div>
          <div className="rounded-md border border-border px-2 py-1.5 text-[10px] text-text">
            All Channels
          </div>
          <div className="rounded-md border border-border px-2 py-1.5 text-[10px] text-text">
            All Statuses
          </div>
        </div>

        {/* Table */}
        <div className="mt-2.5 overflow-hidden rounded-xl border border-border bg-surface">
          <div className="grid grid-cols-[1.1fr_0.9fr_1.1fr_0.7fr_0.9fr] gap-2 border-b border-border bg-surface-2/60 px-3 py-2 text-[9px] font-semibold uppercase tracking-wide text-text-soft">
            <span>ID</span>
            <span>Channel</span>
            <span>User</span>
            <span>Msgs</span>
            <span>Status</span>
          </div>
          {ROWS.map((r) => (
            <div
              key={r.id}
              className="grid grid-cols-[1.1fr_0.9fr_1.1fr_0.7fr_0.9fr] items-center gap-2 border-b border-border/60 px-3 py-2 text-[10px]"
            >
              <span className="truncate font-mono text-brand-blue">{r.id}</span>
              <span>
                <ChannelChip channel={r.channel} />
              </span>
              <span className="truncate text-text-strong">{r.name}</span>
              <span className="text-muted">{r.msgs}</span>
              <span>
                <StatusPill status={r.status} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

/* ── Lead profile (contact context) ─────────────────────────────────────── */

/** Lead detail: profile rows + engagement + associated conversations —
 *  mirrors lead-detail.tsx. */
export function LeadMock({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'w-full max-w-sm overflow-hidden rounded-2xl bg-surface shadow-3',
        className,
      )}
    >
      {/* Detail header */}
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <Avatar name="Paul Goodman" tone="bg-brand-blue" className="h-12 w-12 text-base" />
        <div>
          <div className="text-[15px] font-semibold text-text-strong">Paul Goodman</div>
          <div className="mt-1 flex items-center gap-1.5">
            <StatusPill status="new" />
            <span className="text-[9px] font-semibold uppercase tracking-wide text-text-soft">
              WhatsApp
            </span>
          </div>
        </div>
      </div>

      {/* Profile */}
      <div className="px-5 py-4">
        <div className="text-[12px] font-semibold text-text-strong">Profile</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Row icon={Mail} label="Email" value="paul@acme.co" />
          <Row icon={Phone} label="Phone" value="+234 819…" />
          <Row icon={MessagesSquare} label="Channel" value="WhatsApp" />
          <Row icon={User} label="Source" value="Inbound" />
        </div>
      </div>

      {/* Engagement */}
      <div className="border-t border-border px-5 py-4">
        <div className="flex items-center justify-between text-[12px] font-semibold text-text-strong">
          Engagement
          <span className="text-brand-blue">60%</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className="h-full rounded-full"
            style={{ width: '60%', background: 'linear-gradient(90deg,#2D71F3,#3F70EB)' }}
          />
        </div>
        <div className="mt-1 text-[10px] text-text-soft">3 conversations</div>
      </div>

      {/* Associated conversations */}
      <div className="border-t border-border px-5 py-4">
        <div className="text-[12px] font-semibold text-text-strong">Associated conversations</div>
        <div className="mt-2.5 space-y-2">
          {[
            { id: 'cv_a91b', channel: 'WhatsApp', date: 'Jun 18' },
            { id: 'cv_7d4e', channel: 'WhatsApp', date: 'Jun 11' },
          ].map((c) => (
            <div key={c.id} className="flex items-center justify-between text-[10px]">
              <span className="flex items-center gap-1.5">
                <ChannelChip channel={c.channel} />
                <span className="font-mono text-brand-blue">{c.id}</span>
              </span>
              <span className="inline-flex items-center gap-0.5 text-text-soft">
                {c.date}
                <ArrowUpRight className="h-3 w-3" />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Row({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Phone
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-brand-blue">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 leading-tight">
        <div className="text-[9px] uppercase tracking-wide text-text-soft">{label}</div>
        <div className="truncate text-[12px] font-medium text-text-strong">{value}</div>
      </div>
    </div>
  )
}

/* ── Insights (KPIs + handoff-reason donut) ─────────────────────────────── */

const KPIS = [
  { label: 'Total conversations', value: '1,284', sub: '4.2 avg msgs / conv', tone: 'text-brand-navy' },
  { label: 'Handoff rate', value: '12%', sub: '154 handoffs', tone: 'text-brand-gold' },
  { label: 'AI confidence', value: '0.86', sub: 'High', tone: 'text-brand-green' },
]

const HANDOFF_REASONS = [
  { label: 'Out of scope', pct: 42, color: '#2D71F3' },
  { label: 'Low confidence', pct: 28, color: '#FFC20D' },
  { label: 'Customer asked', pct: 18, color: '#1BDE6C' },
  { label: 'Frustration', pct: 12, color: '#FF3E13' },
]

/** Insights: KPI cards over a handoff-reasons donut — mirrors insights.tsx. */
export function InsightsMock({ className }: { className?: string }) {
  // Build the conic-gradient stops for the donut.
  let acc = 0
  const stops = HANDOFF_REASONS.map((r) => {
    const seg = `${r.color} ${acc}% ${acc + r.pct}%`
    acc += r.pct
    return seg
  }).join(', ')

  return (
    <div
      aria-hidden="true"
      className={cn(
        'w-full overflow-hidden rounded-2xl bg-surface shadow-3',
        className,
      )}
    >
      {/* KPI row */}
      <div className="grid grid-cols-3 gap-px border-b border-border bg-border">
        {KPIS.map((k) => (
          <div key={k.label} className="bg-surface px-3 py-3">
            <div className="text-[9px] uppercase tracking-wide text-text-soft">{k.label}</div>
            <div className={cn('mt-1 text-lg font-semibold', k.tone)}>{k.value}</div>
            <div className="text-[9px] text-text-soft">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Handoff reasons donut */}
      <div className="px-5 py-5">
        <div className="text-[12px] font-semibold text-text-strong">Handoff reasons</div>
        <div className="mt-1 text-[10px] text-text-soft">Why conversations escalated to humans</div>
        <div className="mt-4 flex items-center gap-5">
          <div
            className="relative h-28 w-28 shrink-0 rounded-full"
            style={{ background: `conic-gradient(${stops})` }}
          >
            <div className="absolute inset-[22%] flex flex-col items-center justify-center rounded-full bg-surface">
              <span className="text-[15px] font-semibold text-text-strong">154</span>
              <span className="text-[8px] text-text-soft">handoffs</span>
            </div>
          </div>
          <div className="flex-1 space-y-2">
            {HANDOFF_REASONS.map((r) => (
              <div key={r.label} className="flex items-center justify-between text-[11px]">
                <span className="inline-flex items-center gap-1.5 text-text">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: r.color }} />
                  {r.label}
                </span>
                <span className="font-semibold text-text-strong">{r.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
